import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { playSound, Sound } from '~/assets/sounds';
import { VehicleEngineType } from '~/shared';
import {
	StyledBackground,
	StyledBlueBg,
	StyledContainer,
	StyledMaxButton,
	StyledSlider,
	StyledSliderBackgroundWrapper,
	StyledSliderContainer,
	StyledValueContainer,
} from './FuelSlider.styled';

interface IFuelSliderProps {
	min: number;
	max: number;
	fuelSliderValue: number;
	engineType: VehicleEngineType;
	onChange: (newValue: number) => void;
}

const FuelSlider = ({
	min,
	max,
	fuelSliderValue,
	onChange,
	engineType,
}: IFuelSliderProps) => {
	const [value, setValue] = useState(0);
	const sliderRef = useRef(null);

	const remainingExact = useMemo(() => Math.max(0, max - min), [max, min]);
	const hasAtLeastOneLiter = useMemo(
		() => remainingExact >= 1,
		[remainingExact],
	);
	const remainingPercent = useMemo(
		() => (max > 0 ? (remainingExact / max) * 100 : 0),
		[remainingExact, max],
	);
	const minTrackPercent = useMemo(
		() => (hasAtLeastOneLiter ? 8 : 0),
		[hasAtLeastOneLiter],
	);
	const trackPercent = useMemo(
		() => Math.min(100, Math.max(remainingPercent, minTrackPercent)),
		[remainingPercent, minTrackPercent],
	);
	const leftPercent = useMemo(() => 100 - trackPercent, [trackPercent]);

	const gradient = useMemo(() => {
		if (min === 0) {
			return '#ffffff 0%,';
		}
		if (min === max) {
			return `#2d70da 0%,
						 #2d70da 100%,`;
		}
		return `#2d70da 0%,
						 #2d70da 5px,
						 #ffffff 5px,`;
	}, [min, max]);

	const applySliderBackground = useCallback(
		(pct: number) => {
			const el = sliderRef.current as HTMLInputElement | null;
			if (!el) {
				return;
			}
			el.style.background = `linear-gradient(
			to right,
			${gradient}
			#ffffff calc(9px + (100% - 20px)*${pct / 100}),
			transparent calc(9px + (100% - 20px)*${pct / 100}),
			transparent 100%
		)`;
		},
		[gradient],
	);

	const onSlide = (e: React.ChangeEvent<HTMLInputElement>) => {
		const next = Number(e.target.value);
		setValue(next);
		applySliderBackground(next);
		const fuelValue = (next / 100) * (max - min);
		onChange(Math.floor(fuelValue));
	};

	const handleMax = useCallback(() => {
		const range = max - min;
		if (range < 1) {
			setValue(0);
			applySliderBackground(0);
			onChange(0);
			return;
		}
		setValue(100);
		applySliderBackground(100);
		onChange(Math.floor(range));
	}, [max, min, onChange, applySliderBackground]);

	useLayoutEffect(() => {
		const range = max - min;
		if (range < 1) {
			setValue(0);
			applySliderBackground(0);
			return;
		}
		const pct = Math.min(
			100,
			Math.max(0, Math.round((fuelSliderValue / range) * 100)),
		);
		setValue(pct);
		applySliderBackground(pct);
	}, [min, max, fuelSliderValue, applySliderBackground]);

	return (
		<StyledSliderBackgroundWrapper $engineType={engineType}>
			<StyledBackground $engineType={engineType} />
			{min === max || remainingExact < 1 ? (
				<StyledBlueBg
					style={{
						width: `${engineType === VehicleEngineType.Petrol ? 290 : 190}px`,
					}}
				/>
			) : (
				<StyledBlueBg
					style={{
						width: `${
							(leftPercent / 100) *
								(engineType === VehicleEngineType.Petrol ? 250 : 150) +
							(min === 0 ? 20 : 25)
						}px`,
					}}
				/>
			)}
			<StyledContainer>
				<StyledSliderContainer>
					<div
						style={{
							width: `${max - min !== 0 ? leftPercent : 97}%`,
						}}
					/>
					<StyledSlider
						ref={sliderRef}
						value={value}
						onChange={onSlide}
						style={{
							width: `${max - min !== 0 ? trackPercent : 0}%`,
						}}
						disabled={remainingExact < 1}
					/>
				</StyledSliderContainer>
			</StyledContainer>
			<StyledMaxButton
				onClick={() => {
					handleMax();
					playSound(Sound.ClickButton);
				}}
				onMouseEnter={() => playSound(Sound.LinkCursor)}
				tabIndex={-1}
			>
				Max
			</StyledMaxButton>
			<StyledValueContainer>
				<span style={{ fontWeight: 500 }}>
					{remainingExact < 1 ? 0 : fuelSliderValue}
				</span>
				&nbsp;
				<span style={{ opacity: 0.5 }}>
					{engineType === VehicleEngineType.Electric ? '%' : 'л.'}
				</span>
			</StyledValueContainer>
		</StyledSliderBackgroundWrapper>
	);
};

export { FuelSlider };
